// app/projects/CompetencyPerformance.jsx
import { useMemo, useState } from 'react';
import { SCORE_MARK } from '../ds';
import { scoreBand } from './scoring';
import { cn } from '../../lib/cn';

function avgOrNull(values) {
  const vs = values.filter((v) => v != null);
  if (!vs.length) return null;
  return vs.reduce((a, b) => a + b, 0) / vs.length;
}

/* Group skills by competency, compute rolled-up stats */
function buildCompetencies(skills) {
  const map = new Map();
  for (const sk of skills) {
    if (!map.has(sk.competency_id)) {
      map.set(sk.competency_id, {
        competency_id: sk.competency_id,
        competency: sk.competency,
        area: sk.area,
        _skills: [],
      });
    }
    map.get(sk.competency_id)._skills.push(sk);
  }
  return Array.from(map.values()).map((c) => ({
    competency_id: c.competency_id,
    competency: c.competency,
    area: c.area,
    class_avg: avgOrNull(c._skills.map((s) => s.class_avg)),
    n_skills: c._skills.length,
    n_graded_skills: c._skills.filter((s) => s.n_graded > 0).length,
    mastery_pct: (() => {
      const totalGraded = c._skills.reduce((a, s) => a + s.n_graded, 0);
      const totalMastered = c._skills.reduce((a, s) => a + s.n_mastered, 0);
      return totalGraded > 0 ? totalMastered / totalGraded : null;
    })(),
  }));
}

// Bar fills come from the score family (bands → representative steps 1/3/5)
// instead of the status tokens, so "struggling red" here is the same red as
// every chip, cell, and dot on the page. The value TEXT stays in ink — text
// wears text tokens; the colored bar beside it carries the meaning.
const BAR_FILL = {
  low: SCORE_MARK[1],
  mid: SCORE_MARK[3],
  high: SCORE_MARK[5],
  none: null,
};

const SORTS = [
  { key: 'worst', label: 'Worst first' },
  { key: 'best', label: 'Best first' },
  { key: 'name', label: 'A–Z' },
];

export default function CompetencyPerformance({
  skills,
  selectedCompetencyId,
  onSelectCompetency,
}) {
  const [sortBy, setSortBy] = useState('worst');

  const competencies = useMemo(() => {
    const arr = buildCompetencies(skills);
    arr.sort((a, b) => {
      if (sortBy === 'name') return (a.competency || '').localeCompare(b.competency || '');
      const av = a.class_avg == null ? Infinity : a.class_avg;
      const bv = b.class_avg == null ? Infinity : b.class_avg;
      return sortBy === 'worst' ? av - bv : bv - av;
    });
    return arr;
  }, [skills, sortBy]);

  if (competencies.length === 0) {
    return <div className="ds p-5 text-sm text-muted-foreground">No competencies to show.</div>;
  }

  return (
    <div className="ds px-5 pb-4 pt-3.5">
      <div className="mb-2.5 flex items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Sort by
        </span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSortBy(s.key)}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
              sortBy === s.key
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        {competencies.map((c) => {
          const band = scoreBand(c.class_avg);
          const pct = c.class_avg == null ? 0 : (c.class_avg / 5) * 100;
          const isSelected = selectedCompetencyId === c.competency_id;
          return (
            <button
              key={c.competency_id}
              type="button"
              onClick={() => onSelectCompetency?.(isSelected ? null : c.competency_id)}
              title="Click to filter the matrix below to this competency's skill developmental descriptors"
              className={cn(
                'grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_auto_auto] items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40 hover:bg-secondary/40'
              )}
            >
              <div className="min-w-0">
                {/* full text at rest — wrap to two lines rather than truncating */}
                <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">
                  {c.competency}
                </div>
                <div className="line-clamp-2 text-[11px] text-muted-foreground">{c.area}</div>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                {/* square at the baseline (left), rounded only at the data end —
                    a near-zero average reads as a sliver, not a floating dot */}
                <div
                  className={cn(
                    'h-full rounded-r-full transition-[width]',
                    band === 'none' && 'bg-muted-foreground/25'
                  )}
                  style={{ width: `${pct}%`, background: BAR_FILL[band] ?? undefined }}
                />
              </div>
              {c.n_graded_skills > 0 ? (
                <div className="flex items-baseline gap-1 tabular-nums">
                  <span className="text-[10px] uppercase text-muted-foreground">Avg</span>
                  <span className="text-sm font-bold text-foreground">
                    {c.class_avg != null ? c.class_avg.toFixed(1) : '—'}
                  </span>
                </div>
              ) : (
                <span className="text-xs italic text-muted-foreground">Not assessed</span>
              )}
              <div
                className="whitespace-nowrap text-[11px] text-muted-foreground"
                title="Skill developmental descriptors assessed out of total"
              >
                {c.n_graded_skills}/{c.n_skills} assessed
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
